/**
 * «Cómo pagar»: los datos para transferir, donde se necesitan.
 *
 * Vive en Cobros y no en «Más» (pedido del dueño, 8-set-2026): es información
 * que se busca en el momento de pagar, y buscarla implicaba salir de la
 * pantalla del cobro, entrar a Más y bajar hasta el fondo.
 *
 * ── Qué se muestra y qué no ────────────────────────────────────────────────
 *
 * Solo los dos números que alguien va a pegar en la app del banco: el SINPE y
 * la cuenta (con su IBAN). El banco y el titular NO van como filas: repetir
 * «BAC Credomatic» y «Clean Blue Ltda.» en cada renglón llenaba la tarjeta de
 * texto que nadie copia y escondía lo que sí. Van una sola vez, al pie, como
 * la aclaración que son — y ahí queda dicho que **las dos cuentas son del
 * mismo banco**, que era lo que no se entendía.
 *
 * Los datos salen de la configuración, no del código: si cambia la cuenta se
 * cambia en un lugar.
 */

import { useState } from 'react';
import { useApi } from '@/lib/hooks';
import { Async, Button, Empty, Modal } from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';

interface PaymentInfo {
  /* El worker responde `payment_info: null` cuando nadie lo configuró — y un
     200 con null NO es lo mismo que un 500 o una red caída. */
  settings: { payment_info: Record<string, string> | null };
}

/* El orden es el orden de uso: primero SINPE, que es lo que casi todo el mundo
   usa desde el celular.

   `copiaSinCR` existe solo para el IBAN: la app del banco ya trae el «CR»
   puesto y espera los veinte dígitos, así que pegarlo entero obliga a borrar
   dos letras a mano (dueño, 8-set-2026). Se saca únicamente el código de país;
   los dos dígitos que siguen NO son parte del «CR» —son los de control del
   IBAN— y esos sí se copian. */
const NUMEROS: { clave: string; label: string; copiaSinCR?: boolean }[] = [
  { clave: 'sinpe_phone', label: 'SINPE Móvil' },
  { clave: 'bank_account', label: 'Cuenta' },
  { clave: 'bank_iban', label: 'IBAN', copiaSinCR: true },
];

/** Quita el código de país del frente. `CR7301…` → `7301…`. */
const soloDigitos = (v: string) => v.replace(/^\s*[A-Za-z]+/, '');

export function ComoPagarBoton() {
  const [abierto, setAbierto] = useState(false);

  return (
    <>
      <Button onClick={() => setAbierto(true)}>Cómo pagar</Button>
      {abierto && <ComoPagarModal onClose={() => setAbierto(false)} />}
    </>
  );
}

function ComoPagarModal({ onClose }: { onClose: () => void }) {
  const settings = useApi<PaymentInfo>('/portal/payment-info', { retry: false });

  return (
    <Modal
      title="Cómo pagar"
      onClose={onClose}
      footer={<Button variant="primary" onClick={onClose}>Listo</Button>}
    >
      {/* `Async` separa los tres casos: cargando, error de red (con
          «Reintentar») y vacío de verdad. Antes cualquier fallo se leía como
          «datos de pago no configurados», y el cliente llamaba a preguntar por
          algo que sí estaba configurado. */}
      <Async query={settings} empty={(d) => !d.settings?.payment_info && (
        <Empty icon="cobros" title="Datos de pago no configurados" hint="Consultá con administración." />
      )}>
        {(data) => {
          const info = data.settings.payment_info ?? {};
          const filas = NUMEROS.filter((n) => info[n.clave]);
          /* Sin el punto final del valor guardado: la razón social viene como
             «CLEAN BLUE LTDA.» y al meterla en una frase quedaban dos puntos. */
          const sinPunto = (v?: string) => v?.replace(/\.\s*$/, '');
          const banco = sinPunto(info.bank_name);
          const titular = sinPunto(info.sinpe_name ?? info.holder);

          return (
            <>
              <div className="mcard mcard--par">
                {filas.map((n, i) => (
                  <div key={n.clave} className="mrow" style={i === filas.length - 1 ? { borderBottom: 0 } : undefined}>
                    <span className="mrow__l">{n.label}</span>
                    {/* Se muestra completo y se copia sin el «CR»: el número a
                        la vista sirve para verificarlo contra el banco, y el
                        que se pega es el que la app espera. */}
                    <span className="mrow__r mono">
                      {info[n.clave]}
                      <CopiarBoton
                        valor={n.copiaSinCR ? soloDigitos(info[n.clave]!) : info[n.clave]!}
                        titulo={n.copiaSinCR ? 'Copiar sin el CR' : undefined}
                      />
                    </span>
                  </div>
                ))}
              </div>

              {(banco || titular) && (
                <p className="small muted mt-2">
                  {banco && titular
                    ? <>Las dos cuentas son de <strong>{banco}</strong>, a nombre de <strong>{titular}</strong>.</>
                    : banco
                      ? <>Las dos cuentas son de <strong>{banco}</strong>.</>
                      : <>A nombre de <strong>{titular}</strong>.</>}
                </p>
              )}
            </>
          );
        }}
      </Async>
    </Modal>
  );
}

function CopiarBoton({ valor, titulo }: { valor: string; titulo?: string }) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch {
      // Sin permiso del portapapeles: el número ya se ve tal cual, así que
      // copiarlo a mano sigue funcionando — no hace falta avisar del error.
    }
  }

  return (
    <button
      type="button"
      className="copiar-btn"
      onClick={() => void copiar()}
      title={titulo}
      aria-label={copiado ? 'Copiado' : (titulo ?? `Copiar ${valor}`)}
    >
      <Icon name={copiado ? 'confirmar' : 'documento'} size={14} />
    </button>
  );
}
